import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Button,
    Box,
    FormControl,
    FormControlLabel,
    FormLabel,
    makeStyles,
    Menu,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
} from '@material-ui/core';
import i18n from 'i18n-js';
import { useAppSelector } from 'index';

import { ReservationCancellationReason, ReservationDTO } from 'api/controllers/reservations';
import { PopupConfirmation } from 'components/shared';
import { theme } from 'config/theme';
import {
    formatGuestName,
    formatGuestsCount,
    formatGuestsLabel,
    formatReservationDate,
} from 'helpers/reservationHelper';
import globalStore from 'store/globalStore';
import reservationsStore from 'store/reservationsStore';
import { ReservationsTabFilter, useThunkDispatch } from '../hooks';

const useStyles = makeStyles({
    declineOption: {
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        width: 260,
        padding: '10px 24px',
    },
    declinePill: {
        minWidth: 100,
        textAlign: 'center',
        fontSize: 14,
        lineHeight: '18px',
        borderRadius: 100,
        border: `1px solid ${theme.palette.error.main}`,
        color: theme.palette.error.main,
        backgroundColor: theme.palette.common.white,
    },
    declineHelper: { fontSize: 12, color: theme.palette.text.disabled },
    declineReason: {
        '&& .MuiFilledInput-root': {
            padding: '24px 12px 10px',
        },
        '& .MuiFormHelperText-root': {
            display: 'block',
            margin: '4px 0 0',
        },
    },
});

export interface ProcessRequestLocationState {
    activeFilter?: ReservationsTabFilter;
    reservation?: ReservationDTO;
}

export default function RequestActions({
    reservation,
    activeFilter,
}: {
    reservation: ReservationDTO;
    activeFilter?: ReservationsTabFilter;
}) {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useThunkDispatch();
    const refreshStatus = useAppSelector(
        (state) => state.reservations.requestRefresh[reservation.id]
    );
    const requestClosurePending = useAppSelector(
        (state) => state.reservations.requestClosurePending[reservation.id]
    );
    const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
    const [dialog, setDialog] =
        useState<{
            action: 'decline' | 'cancel';
            reservation: ReservationDTO;
        } | null>(null);
    const [reason, setReason] = useState('');
    const [cancelReason, setCancelReason] =
        useState<ReservationCancellationReason>('GuestCancelled');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isBusy = isSubmitting || !!requestClosurePending;
    const submitting = useRef(false);
    const mounted = useRef(true);
    const normalizedReason = reason.trim();
    const reasonTooLong = normalizedReason.length > 300;

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            dispatch({ type: 'RESERVATION_REQUEST_DIALOG', id: null });
        };
    }, [dispatch]);

    useEffect(() => {
        if (refreshStatus) {
            setDialog(null);
            setMoreAnchor(null);
            setReason('');
            setCancelReason('GuestCancelled');
        }
    }, [refreshStatus]);

    function closeDialog() {
        if (submitting.current) return;
        setDialog(null);
        setReason('');
        setCancelReason('GuestCancelled');
        dispatch({ type: 'RESERVATION_REQUEST_DIALOG', id: null });
    }

    function openDialog(action: 'decline' | 'cancel') {
        if (refreshStatus || requestClosurePending) return;
        setMoreAnchor(null);
        setReason('');
        setCancelReason('GuestCancelled');
        setDialog({ action, reservation: { ...reservation } });
        dispatch({ type: 'RESERVATION_REQUEST_DIALOG', id: reservation.id });
    }

    const save = async () => {
        if (
            refreshStatus ||
            requestClosurePending ||
            submitting.current ||
            !dialog ||
            (dialog.action === 'decline' && reasonTooLong)
        )
            return;
        submitting.current = true;
        setIsSubmitting(true);
        try {
            const target = dialog.reservation;
            const result = await dispatch(
                dialog.action === 'decline'
                    ? reservationsStore.actionCreators.declineRequest(target.id, {
                          expectedVersion: target.version ?? 0,
                          reason: normalizedReason || null,
                      })
                    : reservationsStore.actionCreators.cancelRequest(target.id, {
                          expectedVersion: target.version ?? 0,
                          cancellationReason: cancelReason,
                      })
            );
            if (result.status !== 'alreadyHandled')
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t(
                            dialog.action === 'decline'
                                ? 'reservations.requestDeclined'
                                : 'reservations.requestCancelled'
                        )
                    )
                );
            if (result.status !== 'alreadyHandled' && result.cancellationEmailFailed) {
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'error',
                        i18n.t('reservations.requestCancelEmailFailed')
                    )
                );
            }
            if (mounted.current) {
                setDialog(null);
                setReason('');
                setCancelReason('GuestCancelled');
            }
        } catch {
            dispatch(
                globalStore.actionCreators.showToaster('error', i18n.t('reservations.couldNotSave'))
            );
        } finally {
            submitting.current = false;
            if (mounted.current) setIsSubmitting(false);
        }
    };

    if (reservation.status !== 'Requested' || reservation.isArchived) return null;

    if (refreshStatus) {
        return (
            <Box role="status">
                <span>
                    {i18n.t(
                        refreshStatus === 'failed'
                            ? 'reservations.handledRefreshFailed'
                            : 'reservations.handledRefreshing'
                    )}
                </span>
                <Button
                    color="primary"
                    disabled={refreshStatus === 'loading'}
                    onClick={() =>
                        dispatch(
                            reservationsStore.actionCreators.refreshHandledRequest(reservation.id)
                        )
                    }
                >
                    {i18n.t('reservations.retryRefresh')}
                </Button>
            </Box>
        );
    }

    const target = dialog?.reservation ?? reservation;
    const hasEmail = !!target.email?.trim();
    const descriptionValues = {
        name: formatGuestName(target),
        dateTime: formatReservationDate(target.reservationStartDateTime, 'EEE dd.MM.yyyy, HH:mm'),
        guests: formatGuestsLabel(formatGuestsCount(target)),
    };

    return (
        <>
            <Button
                variant="contained"
                color="primary"
                disabled={isBusy}
                onClick={(event) => setMoreAnchor(event.currentTarget)}
            >
                {i18n.t('reservations.more')}
            </Button>
            <Button
                variant="contained"
                color="primary"
                disabled={isBusy}
                onClick={() =>
                    history.push(`/reservations/requests/${reservation.id}/process`, {
                        activeFilter,
                        reservation,
                    })
                }
            >
                {i18n.t('reservations.processRequest')}
            </Button>
            <Menu
                anchorEl={moreAnchor}
                open={!!moreAnchor}
                onClose={() => setMoreAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <MenuItem className={classes.declineOption} onClick={() => openDialog('decline')}>
                    <span className={classes.declinePill}>{i18n.t('reservations.decline')}</span>
                    <span className={classes.declineHelper}>
                        {i18n.t('reservations.declineHelper')}
                    </span>
                </MenuItem>
                <MenuItem className={classes.declineOption} onClick={() => openDialog('cancel')}>
                    <span className={classes.declinePill}>{i18n.t('reservations.cancel')}</span>
                    <span className={classes.declineHelper}>
                        {i18n.t('reservations.cancelRequestHelper')}
                    </span>
                </MenuItem>
            </Menu>
            <PopupConfirmation
                open={dialog?.action === 'decline'}
                isDismissible
                actionVariant="contained"
                close={closeDialog}
                title={i18n.t('reservations.declineTitle')}
                description={i18n.t(
                    hasEmail
                        ? 'reservations.declineDescription'
                        : 'reservations.declineNoEmailDescription',
                    descriptionValues
                )}
                activeBtn={i18n.t('reservations.decline')}
                isSubmitting={isBusy}
                isActionDisabled={reasonTooLong}
                keepOpenOnAction
                preventDismissWhileSubmitting
                action={save}
            >
                <TextField
                    id="request-decline-reason"
                    className={classes.declineReason}
                    fullWidth
                    multiline
                    rows={3}
                    variant="filled"
                    InputLabelProps={{ shrink: true }}
                    label={i18n.t('reservations.declineReason')}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    disabled={isBusy}
                    error={reasonTooLong}
                    helperText={i18n.t(
                        reasonTooLong
                            ? 'reservations.declineReasonError'
                            : 'reservations.declineReasonHelper'
                    )}
                />
            </PopupConfirmation>
            <PopupConfirmation
                open={dialog?.action === 'cancel'}
                isDismissible
                close={closeDialog}
                title={i18n.t('reservations.cancelRequestTitle')}
                description={
                    i18n.t('reservations.cancelRequestDescription', descriptionValues) +
                    (hasEmail ? '' : ` ${i18n.t('reservations.cancelNoEmailHint')}`)
                }
                cancelLabel={i18n.t('reservations.cancelBack')}
                activeBtn={i18n.t('reservations.cancelRequestConfirm')}
                isSubmitting={isBusy}
                keepOpenOnAction
                preventDismissWhileSubmitting
                action={save}
            >
                <FormControl component="fieldset" disabled={isBusy}>
                    <FormLabel component="legend">
                        {i18n.t('reservations.requestCancelReason')}
                    </FormLabel>
                    <RadioGroup
                        aria-label={i18n.t('reservations.requestCancelReason')}
                        value={cancelReason}
                        onChange={(event) => {
                            const value = event.target.value;
                            if (
                                value === 'GuestCancelled' ||
                                value === 'RestaurantClosed' ||
                                value === 'Other'
                            ) {
                                setCancelReason(value);
                            }
                        }}
                    >
                        {(['GuestCancelled', 'RestaurantClosed', 'Other'] as const).map((value) => (
                            <FormControlLabel
                                key={value}
                                value={value}
                                control={<Radio color="primary" />}
                                label={i18n.t(`reservations.cancelReasons.${value}`)}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </PopupConfirmation>
        </>
    );
}
